import { v4 as uuidv4 } from 'uuid';
import { P2GBox, P2GButton, P2GGrid, P2GIcon, P2GLink, P2GText, P2GTextField, SvgDeleteBin } from '@parcel2go/core-ui';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import useSWR from 'swr';
import { QUOTE_API, TranslationNamespaces } from '~/lib/constants';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';
import { CountryResponse, QuoteParcel } from '~/lib/types/basket-types';
import { MenuItem } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { QuoteFilterSectionTitle } from './quote-filter-section';
import { IP2GTheme } from '@parcel2go/core-ui/dist/types';
import { PublicSettings } from '~/lib/ui-serverless-settings';
import { displayCurrencySymbol } from '~/lib/prices';
import { useCountryTranslations } from '~/hooks/use-country-translations';
import { PrinterType, QuoteFilterOptions, quoteRequestParams, ServiceType, SortType } from '~/lib/quotes';
import { useSearchParams } from 'react-router-dom';

type QuoteEditorProps = {
  editQuote: boolean;
  onEditQuote: (editing: boolean) => void;
  origin: string;
  destination: string;
  filter: QuoteFilterOptions;
  updateFilter: (filter: QuoteFilterOptions) => void;
  parcels?: QuoteParcel[];
  originPostcode?: string;
  destinationPostcode?: string;
  onChange: (
    origin: string,
    destination: string,
    parcels: QuoteParcel[],
    originPostcode?: string,
    destinationPostcode?: string,
    serviceType?: ServiceType,
    sortType?: SortType,
    printerTypes?: PrinterType[],
    estimatedDeliveryDays?: number[],
    ratings?: number[],
    incVat?: boolean
  ) => void;
};

function QuoteEditor({
  onChange,
  onEditQuote,
  editQuote,
  origin,
  originPostcode,
  destination,
  destinationPostcode,
  parcels,
  filter,
  updateFilter
}: QuoteEditorProps) {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const { tenant, settings } = useTenant();
  const [searchParams] = useSearchParams();
  const { session } = useAnalytics();
  const [state, setValues] = useState({
    origin,
    destination,
    length,
    originPostcode,
    destinationPostcode,
    parcels: parcels || [],
    serviceType: filter.serviceType,
    sortType: filter.sortType,
    printerTypes: filter.printerTypes,
    estimatedDeliveryDays: filter.estimatedDeliveryDays,
    ratings: filter.ratings,
    incVat: filter.incVat
  });

  const sameDayServiceType = filter.serviceType === 'SameDay';
  const { translateCountry } = useCountryTranslations();

  const disabledSameDay = useMemo(() => {
    if (!sameDayServiceType) return false;

    return !state.originPostcode || !state.destinationPostcode || state.origin !== state.destination;
  }, [sameDayServiceType, state.originPostcode, state.destinationPostcode, state.origin, state.destination]);

  const onUpdate = useCallback(() => {
    onEditQuote(false);
    onChange(
      state.origin,
      state.destination,
      state.parcels,
      state.originPostcode,
      state.destinationPostcode,
      filter.serviceType,
      filter.sortType,
      filter.printerTypes,
      filter.estimatedDeliveryDays,
      filter.ratings,
      filter.incVat
    );
  }, [state, filter, onChange]);

  const onReset = useCallback(() => {
    onEditQuote(false);

    if (sameDayServiceType) {
      const { quoteType } = quoteRequestParams(searchParams, settings);

      if (quoteType !== 'SameDay') {
        updateFilter({ ...filter, serviceType: undefined });
      }
    }

    setValues({
      origin,
      destination,
      length,
      originPostcode,
      destinationPostcode,
      parcels: parcels || [],
      serviceType: filter.serviceType,
      sortType: filter.sortType,
      printerTypes: filter.printerTypes,
      estimatedDeliveryDays: filter.estimatedDeliveryDays,
      ratings: filter.ratings,
      incVat: filter.incVat
    });
  }, [origin, destination, length, originPostcode, destinationPostcode, parcels, setValues, filter.serviceType]);

  const origins = useSWR<CountryResponse>(
    `${QUOTE_API}/countries/origins`,
    (url: string) =>
      fetchWithTenantAnalytics(tenant, settings, session, url)
        .then((res) => res.json())
        .catch(capturePromiseException),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const sortedOriginCountries = useMemo(() => {
    if (!origins.data || !origins.data.result?.countries || origins.data.result?.countries?.length === 0) return [];

    const positioned = origins.data.result.countries
      .filter((item) => item.metadata.position !== undefined)
      .sort((a, b) => (a.metadata.position || 0) - (b.metadata.position || 0));

    const unpositioned = origins.data.result.countries
      .filter((item) => item.metadata.position === undefined)
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return positioned.concat(unpositioned);
  }, [origins.data]);

  const destinations = useSWR<CountryResponse>(
    `${QUOTE_API}/countries/destinations?origin=${origin}`,
    (url: string) =>
      fetchWithTenantAnalytics(tenant, settings, session, url)
        .then((res) => res.json())
        .catch(capturePromiseException),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const sortedDestinationCountries = useMemo(() => {
    if (!destinations.data || !destinations.data.result?.countries || destinations.data.result?.countries?.length === 0)
      return [];

    const positioned = destinations.data.result.countries
      .filter((item) => item.metadata.position !== undefined)
      .sort((a, b) => (a.metadata.position || 0) - (b.metadata.position || 0));

    const unpositioned = destinations.data.result.countries
      .filter((item) => item.metadata.position === undefined)
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return positioned.concat(unpositioned);
  }, [destinations.data]);

  const originName = useMemo(() => {
    return origins.data?.result?.countries.find((item) => item.iso3Code === origin)?.name;
  }, [origin, origins.data]);

  const destinationName = useMemo(() => {
    return destinations.data?.result?.countries.find((item) => item.iso3Code === destination)?.name;
  }, [destination, destinations.data]);

  if (!editQuote) {
    return (
      <P2GGrid container spacing={1} direction="column">
        <P2GGrid item>
          <P2GText variant="body2">
            <QuoteFilterSectionTitle title={t('FILTER_FROM_LABEL', { defaultValue: 'From: ' })} />
            {originName || origin}
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          <P2GText variant="body2">
            <QuoteFilterSectionTitle title={t('FILTER_TO_LABEL', { defaultValue: 'To: ' })} />
            {destinationName || destination}
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          {(parcels?.length || 0) > 5 ? (
            <>
              <QuoteFilterSectionTitle
                title={t('FILTER_PARCEL_MULTIPLE_LABEL', {
                  defaultValue: 'Multi Parcels:'
                })}
              />
              <P2GText variant="body2"> x{parcels?.length}</P2GText>
            </>
          ) : (
            <>
              {parcels?.map((parcel) => {
                const { weight, length, width, height } = parcel;
                if (length && width && height) {
                  return (
                    <P2GBox>
                      <P2GText variant="body2">
                        <QuoteFilterSectionTitle title={t('FILTER_PARCEL_LABEL', { defaultValue: 'Parcel Size: ' })} />{' '}
                        {weight}
                        kg | {length}cm x {width}cm x {height}cm
                      </P2GText>
                    </P2GBox>
                  );
                }

                return (
                  <P2GBox>
                    <P2GText>
                      <QuoteFilterSectionTitle title={t('FILTER_PARCEL_LABEL', { defaultValue: 'Parcel Size: ' })} />{' '}
                      {weight}
                      kg
                    </P2GText>
                  </P2GBox>
                );
              })}
            </>
          )}
        </P2GGrid>
        <P2GGrid item>
          <P2GLink variant="body2" onClick={() => onEditQuote(true)}>
            {t('EDIT_QUOTE_BUTTON', { defaultValue: 'Edit Quote' })}
          </P2GLink>
        </P2GGrid>
      </P2GGrid>
    );
  }

  return (
    <P2GGrid container gap={2}>
      {disabledSameDay && (
        <P2GGrid item xs={12}>
          <P2GText variant="body2" color="error.main">
            {t('EDIT_QUOTE_SAME_DAY_INSTRUCTIONS', {
              defaultValue: 'Please enter both postcodes and update for a Same Day quote.'
            })}
          </P2GText>
        </P2GGrid>
      )}
      <P2GGrid item xs={12}>
        <P2GTextField
          label={t('EDIT_QUOTE_ORIGIN_LABEL', { defaultValue: 'Origin' })}
          select
          fullWidth
          value={state.origin}
          onChange={(e) => setValues({ ...state, origin: e.target.value })}
          SelectProps={{
            MenuProps: {
              style: { zIndex: 10099 }
            }
          }}
        >
          {sortedOriginCountries.map((item) => (
            <MenuItem key={item.name} value={item.iso3Code}>
              {translateCountry(item)}
            </MenuItem>
          ))}
        </P2GTextField>
      </P2GGrid>
      <P2GGrid item xs={12}>
        <P2GTextField
          label={t('EDIT_QUOTE_ORIGIN_POSTCODE_LABEL', { defaultValue: 'Origin Postcode' })}
          step={1}
          fullWidth
          value={state.originPostcode}
          min={1}
          required={sameDayServiceType}
          onChange={(e) => setValues({ ...state, originPostcode: e.target.value })}
        />
      </P2GGrid>
      <P2GGrid item xs={12}>
        <P2GTextField
          label={t('EDIT_QUOTE_DESTINATION_LABEL', { defaultValue: 'Destination' })}
          select
          fullWidth
          value={state.destination}
          onChange={(e) => setValues({ ...state, destination: e.target.value })}
          SelectProps={{
            MenuProps: {
              style: { zIndex: 10099 }
            }
          }}
        >
          {sortedDestinationCountries.map((item) => (
            <MenuItem key={item.name} value={item.iso3Code}>
              {translateCountry(item)}
            </MenuItem>
          ))}
        </P2GTextField>
      </P2GGrid>
      <P2GGrid item xs={12}>
        <P2GTextField
          label={t('EDIT_QUOTE_DESTINATION_POSTCODE_LABEL', { defaultValue: 'Destination Postcode' })}
          step={1}
          fullWidth
          value={state.destinationPostcode}
          min={1}
          required={sameDayServiceType}
          onChange={(e) => setValues({ ...state, destinationPostcode: e.target.value })}
        />
      </P2GGrid>
      <P2GGrid item xs={12}>
        <QuoteFilterSectionTitle
          title={t('EDIT_QUOTE_PARCEL_DIMENSIONS_LABEL', { defaultValue: 'Parcel Dimensions' })}
        />
      </P2GGrid>
      <P2GGrid item xs={12}>
        <P2GGrid container spacing={2}>
          {state.parcels.map((parcel, index) => {
            const { weight, length, width, height, value } = parcel;
            return (
              <P2GGrid item xs={12}>
                <P2GGrid container spacing={2}>
                  <P2GGrid item xs={6}>
                    <P2GTextField
                      label={t('PARCEL_FIELD_LENGTH_LABEL', { defaultValue: 'Length' })}
                      endAdornment="cm"
                      type="number"
                      step={1}
                      fullWidth
                      value={length || ''}
                      min={1}
                      onChange={(e) => {
                        const newParcels = [...state.parcels];
                        newParcels[index] = { ...parcel, length: parseInt(e.target.value) };
                        setValues({ ...state, parcels: newParcels });
                      }}
                      InputLabelProps={{
                        shrink: undefined
                      }}
                    />
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <P2GTextField
                      label={t('PARCEL_FIELD_WIDTH_LABEL', { defaultValue: 'Width' })}
                      endAdornment="cm"
                      type="number"
                      step={1}
                      fullWidth
                      value={width || ''}
                      min={1}
                      onChange={(e) => {
                        const newParcels = [...state.parcels];
                        newParcels[index] = { ...parcel, width: parseInt(e.target.value) };
                        setValues({ ...state, parcels: newParcels });
                      }}
                      InputLabelProps={{
                        shrink: undefined
                      }}
                    />
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <P2GTextField
                      label={t('PARCEL_FIELD_HEIGHT_LABEL', { defaultValue: 'Height' })}
                      endAdornment="cm"
                      type="number"
                      step={1}
                      fullWidth
                      value={height || ''}
                      min={1}
                      onChange={(e) => {
                        const newParcels = [...state.parcels];
                        newParcels[index] = { ...parcel, height: parseInt(e.target.value) };
                        setValues({ ...state, parcels: newParcels });
                      }}
                      InputLabelProps={{
                        shrink: undefined
                      }}
                    />
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <P2GTextField
                      label={t('PARCEL_FIELD_WEIGHT_LABEL', { defaultValue: 'Weight' })}
                      endAdornment="kg"
                      type="number"
                      step={1}
                      fullWidth
                      value={weight}
                      min={1}
                      onChange={(e) => {
                        const newParcels = [...state.parcels];
                        newParcels[index] = { ...parcel, weight: parseFloat(e.target.value) };
                        setValues({ ...state, parcels: newParcels });
                      }}
                      InputLabelProps={{
                        shrink: undefined
                      }}
                    />
                  </P2GGrid>
                  <P2GGrid item xs={12}>
                    <P2GTextField
                      label={t('PARCEL_FIELD_VALUE_LABEL', { defaultValue: 'Contents Value' })}
                      startAdornment={displayCurrencySymbol({
                        code: settings[PublicSettings.CURRENCY] || 'GBP',
                        symbol: ''
                      })}
                      type="number"
                      step={0.01}
                      fullWidth
                      value={value || ''}
                      min={0.01}
                      onChange={(e) => {
                        const newParcels = [...state.parcels];
                        newParcels[index] = { ...parcel, value: parseFloat(e.target.value) };
                        setValues({ ...state, parcels: newParcels });
                      }}
                      InputLabelProps={{
                        shrink: undefined
                      }}
                    />
                  </P2GGrid>
                  {state.parcels.length > 1 && (
                    <P2GGrid item xs={12}>
                      <P2GLink
                        variant="body2"
                        onClick={() => {
                          const newParcels = [...state.parcels];
                          newParcels.splice(index, 1);
                          setValues({ ...state, parcels: newParcels });
                        }}
                        sx={(t: IP2GTheme) => ({ display: 'flex', alignItems: 'center', gap: t.spacing(1) })}
                      >
                        <P2GIcon>
                          <SvgDeleteBin />
                        </P2GIcon>
                        <Trans i18nKey="DELETE_BUTTON" t={t}>
                          Delete
                        </Trans>
                      </P2GLink>
                    </P2GGrid>
                  )}
                </P2GGrid>
              </P2GGrid>
            );
          })}
        </P2GGrid>
      </P2GGrid>
      <P2GGrid item xs={12}>
        <P2GButton
          fullWidth
          type="button"
          variant="tertiary"
          size="large"
          onClick={() => {
            const newParcels = [...state.parcels];
            newParcels.push({
              id: uuidv4(),
              quantity: 1,
              contents: [],
              weight: 1,
              length: null,
              width: null,
              height: null,
              value: null
            });
            setValues({ ...state, parcels: newParcels });
          }}
        >
          <Trans i18nKey="ADD_ANOTHER_BUTTON" t={t}>
            Add Another
          </Trans>
        </P2GButton>
      </P2GGrid>
      <P2GGrid item xs={12}>
        <P2GGrid container gap={2} wrap="nowrap">
          <P2GGrid item xs={6}>
            <P2GButton type={'button'} variant="tertiary" size="large" fullWidth onClick={onReset}>
              {t('RESET_QUOTE_BUTTON', { defaultValue: 'Cancel' })}
            </P2GButton>
          </P2GGrid>
          <P2GGrid item xs={6}>
            <P2GButton
              dataTestId="cta-button"
              id="btnNext"
              type="button"
              size="large"
              fullWidth
              onClick={onUpdate}
              disabled={disabledSameDay}
            >
              {t('UPDATE_QUOTE_BUTTON', { defaultValue: 'Update' })}
            </P2GButton>
          </P2GGrid>
        </P2GGrid>
      </P2GGrid>
    </P2GGrid>
  );
}

export default QuoteEditor;
